import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import CalendarScreen from './screens/CalendarScreen';
import RoutePlanningScreen from './screens/RoutePlanningScreen';
import DeliveryDayClusteringScreen from './screens/DeliveryDayClusteringScreen';
import OptimalRoutesScreen from './screens/OptimalRoutesScreen';
import ScanningScreen from './screens/ScanningScreen';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { logEnvVariables, hasRequiredEnvVars } from './utils/envDebug';
import { H } from 'highlight.run';

H.init('mem5m98g', {
	serviceName: "frontend-app",
	tracingOrigins: true,
	networkRecording: {
		enabled: true,
		recordHeadersAndBody: true,
		urlBlocklist: [
			// insert full or partial urls that you don't want to record here
			// Out of the box, Highlight will not record these URLs (they can be safely removed):
			"https://www.googleapis.com/identitytoolkit",
			"https://securetoken.googleapis.com",
		],
	},
});

function AppContent() {
  const { user } = useAuth();



  return (
    <>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <CalendarScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-planning"
          element={
            <ProtectedRoute>
              <RoutePlanningScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/delivery-clustering"
          element={
            <ProtectedRoute>
              <DeliveryDayClusteringScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/optimal-routes"
          element={
            <ProtectedRoute>
              <OptimalRoutesScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scanning"
          element={
            <ProtectedRoute>
              <ScanningScreen />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  // Log environment variables once when the app loads
  React.useEffect(() => {
    logEnvVariables();
    
    if (!hasRequiredEnvVars()) {
      console.error(
        'Missing required environment variables! ' +
        'This will cause API requests to fail. ' + 
        'Make sure environment variables are properly set during build.'
      );
    }
    
    // Global event listener for when the page is truly unloaded
    const handleBeforeUnload = () => {
      console.log('Page unloading, closing WebSocket connections');
      // This will explicitly close any open WebSocket connections
      if (window._globalWebSocket && window._globalWebSocket.readyState === WebSocket.OPEN) {
        window._globalWebSocket.close(1000, 'Page unloading');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <WebSocketProvider>
      <Router>
        <AuthProvider>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <AppContent />
        </AuthProvider>
      </Router>
    </WebSocketProvider>
  );
}

export default App;
